import * as React from 'react';
import {connect} from "react-redux";
import {Dispatch} from "redux";
import {setPassword} from "../../../store/action/user/user";
import {none, Option, option} from "ts-option";
import {OnFailure, OnSuccess} from "../../../store/action/actionHelpers";
import * as validator from "validator";
import Toast from "../../helper/Toast";
import ButtonPrimary from "../../../hiberUiKit/src/atoms/Buttons/ButtonPrimary";
import "../newPasswordPage.scss";
import Input from "../../../hiberUiKit/src/atoms/Input/Input";

interface IComponentProps {
	/**
	 * Password reset token.
	 */
	token: string;
	/**
	 * On successful change of the password.
	 */
	onSuccess: () => void;
}

interface DispatchProps {
	/**
	 * Set password dispatch.
	 * @param {string} password User password.
	 * @param {string} token Password change token from API.
	 * @param {OnSuccess} onSuccess Success callback.
	 * @param {OnFailure} onFailure Failure callback.
	 */
	setPassword: (password: string, token: string, onSuccess: OnSuccess, onFailure: OnFailure) => void
}

interface State {
	/**
	 * Password
	 */
	password: Option<string>,
	/**
	 * Password
	 */
	passwordConfirm: Option<string>,
	/**
	 * Indicates if logging in is in progress.
	 */
	loading: boolean,
	/**
	 * Error from login attempt.
	 */
	error: Option<string>
	/**
	 * Field with focus.
	 */
	focus: "password" | "passwordConfirm"
}

type Props = IComponentProps & DispatchProps;

class CreateNewPasswordForm extends React.Component<Props, State> {

	constructor(props: Props) {
		super(props);
		this.state = {password: none, passwordConfirm: none, loading: false, error: none, focus: "password"};
		this.updatePassword = this.updatePassword.bind(this);
		this.updatePasswordConfirm = this.updatePasswordConfirm.bind(this);
		this.onSuccess = this.onSuccess.bind(this);
		this.onFailed = this.onFailed.bind(this);
	}

	render() {
		return (
			<React.Fragment>
				<div className="c-new-password_content_form">
					<div className="c-new-password_content_form_field">
						<label htmlFor="new-password">New password</label>
						<Input id="new-password"
							   type={"password"}
							   value={this.state.password.getOrElse("")}
							   onChange={this.updatePassword}
							   placeholder={"Enter new password"}
							   focus={this.state.focus == "password"}
							   onConfirm={() => this.setState({focus: "passwordConfirm"})}/>
					</div>
					<div className="c-new-password_content_form_field">
						<label htmlFor="confirm-password">Confirm password</label>
						<Input id="confirm-password"
							   type={"password"}
							   value={this.state.passwordConfirm.getOrElse("")}
							   onChange={this.updatePasswordConfirm}
							   placeholder={"Enter your password again"}
							   focus={this.state.focus == "passwordConfirm"}
							   onConfirm={() => this.onSetPassword()}/>
					</div>
				</div>
				<div className={"c-new-password_content_error"} role="alert" aria-live="polite">
					{this.renderError()}
				</div>
				<div className={"c-new-password_content_button"}>
					<ButtonPrimary label={"Set password"}
								   variant={"success"}
								   onClick={() => this.onSetPassword()}
								   isLoading={this.state.loading}/>
				</div>
			</React.Fragment>
		);
	}

	/**
	 * Render Sign in error.
	 */
	renderError() {
		return this.state.error.map(() => <span className={"m--font-danger"}>{this.state.error.get}</span>).orNull
	}

	/**
	 * Update username.
	 */
	updatePassword(evt: React.ChangeEvent<HTMLInputElement>) {
		this.setState({password: option(evt.target.value), error: none});
	};

	/**
	 * Update password.
	 */
	updatePasswordConfirm(evt: React.ChangeEvent<HTMLInputElement>) {
		this.setState({passwordConfirm: option(evt.target.value), error: none});
	};

	/**
	 * On sign in click.
	 */
	private onSetPassword() {
		const error = this.validateInputs();
		this.setState({loading: error.isEmpty, error});
		if (error.isEmpty) {
			this.props.setPassword(this.state.password.getOrElse(""), this.props.token, this.onSuccess, this.onFailed)
		}
	}

	/**
	 * Validate inputs.
	 * @returns {Option<string>} Optional error containing error string to display.
	 */
	private validateInputs(): Option<string> {
		const password = this.state.password.getOrElse("");
		const passwordConfirm = this.state.passwordConfirm.getOrElse("");
		if (validator.isEmpty(password) && validator.isEmpty(passwordConfirm))
			return option("Please fill password fields");
		else if (validator.isEmpty(password))
			return option("Please fill password");
		else if (validator.isEmpty(passwordConfirm))
			return option("Please retype password");
		else if (password.length < 5)
			return option("Password is too short");
		else if (password !== passwordConfirm)
			return option("Password does not match the confirm password");
		else
			return none;
	}

	/**
	 * On success password set.
	 */
	private onSuccess() {
		this.setState({loading: false, error: none});
		this.props.onSuccess();
	}

	/**
	 * On filed password set.
	 */
	private onFailed(error: any) {
		let err: Option<string> = none;
		if (error && error.response && error.response.data.status == 404)
			err = option("Token invalid or expired.");
		else
			err = option("Something went wrong. Please try again later");
		this.setState({loading: false, error: err});
	}
}

function mapDispatchToProps(dispatch: Dispatch<any>) {
	return {
		setPassword: (password: string, token: string, onSuccess: OnSuccess, onFailure: OnFailure) => dispatch(
			setPassword(password, token, onSuccess, onFailure)
		)
	}
}

export default connect(null, mapDispatchToProps)(CreateNewPasswordForm)
